import * as React from 'react';
import { cn } from '@/lib/utils';
import ReactDOM from 'react-dom';
import { FiClipboard, FiChevronLeft } from 'react-icons/fi';
import { NotesTabs } from './notes-tabs';
import { useNote } from '@/hooks/useNote';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/hooks/useSession';
import { NotesPrivate } from './notes-private';
import { NotesClassroom } from './notes-classroom';
import { NotesImportant } from './notes-important';
import usePermission from '@/hooks/usePermission';
import { Button, ButtonBack, ButtonBackContent } from '@/components/ui/button';
import { IconWrapper } from '../icon';
import { FilterTitle } from '../filter';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { FormField } from '../form';
import { FieldGroup } from '@/components/ui/field';

export const NotesWrapper = React.forwardRef(({ className, show, hasCustomLogos, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-wrapper',
      // styles
      'fixed bottom-0 right-0 z-[1001] w-[calc(100%-64px)] transition-transform duration-300 ease-in-out',
      'top-[46px] md:top-0 md:w-[960px] md:max-w-[calc(100vw-120px)]',
      show ? 'translate-x-0' : 'translate-x-full',
      hasCustomLogos && 'top-[calc(68px+46px)] md:top-0',
      className
    )}
    {...props}
  />
));
NotesWrapper.displayName = 'NotesWrapper';

export const NotesContainer = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes',
      // styles
      'relative z-0 flex h-full flex-col overflow-y-auto overflow-x-hidden bg-white shadow-[0px_4px_10px_rgba(119,119,120,0.15)]',
      className
    )}
    {...props}
  />
));
NotesContainer.displayName = 'NotesContainer';

export const NotesToggle = React.forwardRef(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'c-notes-toggle',
      // styles
      'absolute left-0 top-0 flex -translate-x-full cursor-pointer appearance-none items-center gap-2 rounded-bl-[10px] border-0 bg-primary p-2 text-[12px] font-bold leading-none text-white transition-all duration-150 hover:shadow-dropshadow-md',
      className
    )}
    {...props}
  />
));
NotesToggle.displayName = 'NotesToggle';

export const NotesToggleText = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-notes-toggle-text',
      // styles
      'hidden md:inline-block',
      className
    )}
    {...props}
  />
));
NotesToggleText.displayName = 'NotesToggleText';

export const NotesBackdrop = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-backdrop',
      // styles
      'fixed left-0 top-0 z-[1000] h-screen w-screen bg-white/40',
      className
    )}
    {...props}
  />
));
NotesBackdrop.displayName = 'NotesBackdrop';

export const NotesHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-header',
      // styles
      'mx-6 mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-neutral-lightest py-6 last:mb-0',
      className
    )}
    {...props}
  />
));
NotesHeader.displayName = 'NotesHeader';

export const NotesBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-body',
      // styles
      'flex flex-grow flex-col gap-4 overflow-y-auto px-6 pb-6 sm:flex-row sm:gap-[68px]',
      className
    )}
    {...props}
  />
));
NotesBody.displayName = 'NotesBody';

export const NotesAside = React.forwardRef(({ className, ...props }, ref) => (
  <aside
    ref={ref}
    className={cn(
      'c-notes-aside',
      // styles
      'flex h-[132px] w-auto flex-shrink-0 flex-col sm:h-auto sm:w-[204px]',
      className
    )}
    {...props}
  />
));
NotesAside.displayName = 'NotesAside';

export const NotesNav = React.forwardRef(({ className, ...props }, ref) => (
  <nav
    ref={ref}
    className={cn(
      'c-notes-nav',
      // styles
      '-mr-4 mt-3 flex h-[328px] flex-grow flex-col gap-3 overflow-y-auto pr-4',
      className
    )}
    {...props}
  />
));
NotesNav.displayName = 'NotesNav';

export const NotesBtn = React.forwardRef(({ className, active, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'c-notes-btn',
      // styles
      'relative flex w-full cursor-pointer appearance-none flex-col rounded border border-solid border-neutral-lightest bg-white p-3 text-left text-[12px] leading-[1.2] text-[#110B3E] shadow-dropshadow',
      active && 'bg-bright',
      className
    )}
    {...props}
  />
));
NotesBtn.displayName = 'NotesBtn';

export const NotesBtnHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-btn-header',
      // styles
      'flex w-full items-center gap-2',
      className
    )}
    {...props}
  />
));
NotesBtnHeader.displayName = 'NotesBtnHeader';

export const NotesBtnText = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-notes-btn-text',
      // styles
      'block text-sm font-bold text-primary-dark',
      className
    )}
    {...props}
  />
));
NotesBtnText.displayName = 'NotesBtnText';

export const NotesBtnDate = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-notes-btn-date',
      // styles
      'block flex-grow truncate text-[12px] font-light text-neutral',
      className
    )}
    {...props}
  />
));
NotesBtnDate.displayName = 'NotesBtnDate';

export const NotesBtnBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-btn-body',
      // styles
      'mb-7 flex-grow',
      className
    )}
    {...props}
  />
));
NotesBtnBody.displayName = 'NotesBtnBody';

export const NotesTag = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-tag',
      // styles
      'absolute right-[10px] top-0',
      className
    )}
    {...props}
  />
));
NotesTag.displayName = 'NotesTag';

export const NotesAuthor = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-author',
      // styles
      'flex items-center gap-2 text-[12px] font-light text-neutral-dark',
      className
    )}
    {...props}
  />
));
NotesAuthor.displayName = 'NotesAuthor';

export const NotesView = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-view',
      // styles
      'flex flex-grow flex-col overflow-hidden',
      className
    )}
    {...props}
  />
));
NotesView.displayName = 'NotesView';

export const NotesViewHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-view-header',
      // styles
      'mb-4 flex flex-shrink-0 items-start gap-3',
      className
    )}
    {...props}
  />
));
NotesViewHeader.displayName = 'NotesViewHeader';

export const NotesViewInnerHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-view-inner-header',
      // styles
      'flex-grow',
      className
    )}
    {...props}
  />
));
NotesViewInnerHeader.displayName = 'NotesViewInnerHeader';

export const NotesViewTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'c-notes-view-title',
      // styles
      'mb-0 text-base font-bold text-primary-dark',
      className
    )}
    {...props}
  />
));
NotesViewTitle.displayName = 'NotesViewTitle';

export const NotesViewDate = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-notes-view-date',
      // styles
      'mb-0 text-[12px] font-light text-neutral',
      className
    )}
    {...props}
  />
));
NotesViewDate.displayName = 'NotesViewDate';

export const NotesViewBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notes-view-body',
      // styles
      'flex h-0 grow flex-col overflow-hidden',
      className
    )}
    {...props}
  />
));

NotesViewBody.displayName = 'NotesViewBody';

export const NotesImportantBtn = React.forwardRef(({ className, added, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'c-notes-important-btn',
      // styles
      'appearance-none rounded border border-solid border-neutral-lightest bg-white px-3 py-[6px] shadow-dropshadow',
      added && '[&_path]:fill-[#FDB462]',
      className
    )}
    {...props}
  />
));
NotesImportantBtn.displayName = 'NotesImportantBtn';

export function Notes(props) {
  const { hasCustomLogos } = props;

  const { t } = useTranslation();
  const { session } = useSession();
  const { createNote, updateNote, activeNote, setActiveNote, getNotes, setNotes, notes, nextBody, activeTab, getNewClassroomNotes, newNotesClasses, showModal, showNotes, setShowModal, setShowNotes, notesModalOpen, setNotesModalOpen } = useNote();

  const [intervalTimer, setIntervalTimer] = React.useState();
  const [filteredNotes, setFilteredNotes] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [viewClassroom, setViewClassroom] = React.useState(false);
  const [classroomData, setClassroomData] = React.useState('');
  const btnTitle = showNotes ? 'Fechar anotações' : 'Abrir anotações';

  const closeOnEscapeKeyDown = event => event.key === 'Escape' && showNotes && setShowNotes(false);

  React.useEffect(() => {
    if (showNotes) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'visible';

    document.body.addEventListener('keydown', closeOnEscapeKeyDown);

    return () => {
      document.body.removeEventListener('keydown', closeOnEscapeKeyDown);
    };
  }, [showNotes]);

  const checkBodyChange = () => {
    updateNote(activeNote, nextBody);
  };

  React.useEffect(() => {
    if (activeNote && activeNote.body !== nextBody && activeNote.body !== '<p className="mb-4"></p>') {
      clearInterval(intervalTimer);
      const newInterval = setTimeout(() => checkBodyChange(), 1000);
      setIntervalTimer(newInterval);
    }

    // getNotes(classroomData);
  }, [nextBody]);

  const form = useFormik({
    initialValues: {
      title: activeNote?.title || '',
      body: activeNote?.body || '',
      'classroom-id': ''
    },
    onSubmit: values => {
      createNote(values);
      setShowModal(false);
      if (viewClassroom) {
        form.setFieldValue('title', '');
      } else {
        form.resetForm();
      }
    }
  });

  const handleOpenNotesModal = () => {
    setShowNotes(!showNotes);
    setNotesModalOpen(true);
  };

  React.useEffect(() => {
    if (notesModalOpen) {
      getNotes(classroomData);
      setActiveNote('');
      getNewClassroomNotes();
    }
  }, [activeTab, classroomData, notesModalOpen]);

  React.useEffect(() => {
    const filterNotes = notes => {
      let filteredNotes = notes;

      if (searchTerm !== '') {
        filteredNotes = filteredNotes.filter(a => a.title != null && a.title.toLowerCase().includes(searchTerm.toLocaleLowerCase()));
      }

      return filteredNotes;
    };

    const newFilteredNotes = filterNotes(notes);
    setFilteredNotes(newFilteredNotes);
  }, [searchTerm, notes]);

  React.useEffect(() => {
    form.setFieldValue('classroom-id', classroomData.id);
  }, [classroomData]);

  const handleNewNote = () => {
    setShowModal(true);
  };

  const hasNotes = notes.length > 0;

  const markedNotes = notes.filter(note => note.bookmarked === true);

  React.useEffect(() => {
    if (!viewClassroom) {
      form.setFieldValue('classroom-id', '');
    }
  }, [viewClassroom]);

  return ReactDOM.createPortal(
    <>
      <NotesWrapper
        show={showNotes}
        hasCustomLogos={hasCustomLogos}
      >
        <NotesToggle
          onClick={handleOpenNotesModal}
          title={btnTitle}
        >
          <FiClipboard size={20} />
          <NotesToggleText>{t('header.notes')}</NotesToggleText>
        </NotesToggle>

        <NotesContainer>
          <NotesHeader>
            {!viewClassroom && <NotesTabs />}

            {viewClassroom && (
              <ButtonBack
                onClick={() => {
                  setViewClassroom(false);
                }}
              >
                <IconWrapper>
                  <FiChevronLeft />
                </IconWrapper>
                <ButtonBackContent>
                  <FilterTitle>{classroomData.title}</FilterTitle>
                </ButtonBackContent>
              </ButtonBack>
            )}
            {!viewClassroom && activeTab !== 'anotacoes-de-turma' && usePermission('Criar anotações', 'Anotações') && (
              <Button
                wide
                type="button"
                onClick={handleNewNote}
              >
                Nova anotação
              </Button>
            )}
            {viewClassroom && usePermission('Criar anotações', 'Anotações') && (
              <Button
                wide
                type="button"
                onClick={handleNewNote}
              >
                Nova anotação pública para turma
              </Button>
            )}
          </NotesHeader>

          {activeTab === 'anotacoes-privadas' && (
            <NotesPrivate
              hasNotes={hasNotes}
              handleNewNote={handleNewNote}
              setSearchTerm={setSearchTerm}
              notes={filteredNotes}
              setActiveNote={setActiveNote}
              session={session}
              activeNote={activeNote}
            />
          )}
          {activeTab === 'anotacoes-de-turma' && (
            <NotesClassroom
              setActiveNote={setActiveNote}
              activeNote={activeNote}
              viewClassroom={viewClassroom}
              classrooms={newNotesClasses}
              setViewClassroom={setViewClassroom}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              setClassroomData={setClassroomData}
              setFilteredNotes={setFilteredNotes}
              setNotes={setNotes}
              notes={notes}
            />
          )}
          {activeTab === 'anotacoes-importantes' && (
            <NotesImportant
              activeNote={activeNote}
              setActiveNote={setActiveNote}
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm}
              notes={markedNotes}
            />
          )}
        </NotesContainer>
      </NotesWrapper>

      {showNotes && <NotesBackdrop onClick={() => setShowNotes(!showNotes)} />}

      <Sheet
        open={showModal}
        onOpenChange={() => {
          setShowModal(false);
        }}
      >
        <SheetContent>
          <SheetHeader />
          <SheetBody>
            <form
              method="post"
              onSubmit={form.handleSubmit}
            >
              <FieldGroup>
                <SheetTitle>{t('header.createNewNote')}</SheetTitle>

                <FormField
                  label={t('header.nameNote')}
                  name="title"
                  type="text"
                  placeholder={t('header.notesPlaceholder')}
                  value={form.values.title}
                  onChange={form.handleChange}
                />

                <Button
                  className="self-start"
                  type="submit"
                >
                  {t('button.saveContinue')}
                </Button>
              </FieldGroup>
            </form>
          </SheetBody>
          <SheetFooter />
        </SheetContent>
      </Sheet>
    </>,
    document.getElementById('modal_root')
  );
}
